import { useCallback, useEffect, useRef, useState } from 'react';
import { 
  TerraDraw, 
  TerraDrawPolygonMode,
  TerraDrawSelectMode
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { TabsType } from '../../enums';
import { useProjectContext } from '@/context/ProjectContext';
import { useMapContext } from '@/context/MapContext';

interface Props {
  map: google.maps.Map | null;
  isLoaded: boolean;
  selectedTab: string;
  isPolygonEditingDisabled?: boolean;
}

const useTerraDrawMarkers = ({ map, selectedTab, isPolygonEditingDisabled = false }: Props) => {
  const [isDrawModeOn, setIsDrawModeOn] = useState<boolean>(false);
  const [isDrawingComplete, setIsDrawingComplete] = useState<boolean>(false);
  const [isBlockingCenterChange, setIsBlockingCenterChange] = useState<boolean>(false);
  const [shapes, setShape] = useState<google.maps.Polygon | null>(null);
  const [marketShapes, setMarketShapes] = useState<google.maps.Polygon | null>(null);

  const {
    setProject,
    project: { id, marketPolygons, soldPolygons },
  } = useProjectContext();

  const { setCoordinates } = useMapContext();

  const terraDrawRef = useRef<TerraDraw | null>(null);
  const currentPolygonRef = useRef<google.maps.Polygon | null>(null);

  // Style configuration for TerraD raw polygons
  const polygonStyle = {
    fillColor: '#10172a',
    fillOpacity: 0.5,
    strokeColor: '#10172a',
    strokeWidth: 2,
    strokeOpacity: 1.0
  };

  const clearDrawMode = useCallback(() => {
    if (terraDrawRef.current) {
      try {
        terraDrawRef.current.stop();
        terraDrawRef.current = null;
      } catch (error) {
        console.warn('Error stopping TerraDraw:', error);
      }
    }
    if (marketShapes) {
      marketShapes.setMap(null);
    }
    if (shapes) {
      shapes.setMap(null);
    }
  }, [shapes, marketShapes]);

  const convertTerraDrawToGooglePolygon = useCallback((coordinates: number[][]) => {
    const polygonCoords = coordinates.map(coord => ({
      lat: coord[1], // Latitude
      lng: coord[0]  // Longitude
    }));

    const polygon = new google.maps.Polygon({
      paths: polygonCoords,
      fillColor: '#10172a',
      strokeColor: '#10172a',
      strokeOpacity: 1.0,
      strokeWeight: 2,
      fillOpacity: 0.5,
      clickable: false,
      editable: !isPolygonEditingDisabled,
      draggable: false,
      zIndex: 1,
    });

    return polygon;
  }, [isPolygonEditingDisabled]);

  const handlePolygonComplete = useCallback(
    (coordinates: number[][]) => {
      if (coordinates.length > 0) {
        const closedPath = [...coordinates, coordinates[0]];
        
        // Create Google Maps polygon for display and editing
        const googlePolygon = convertTerraDrawToGooglePolygon(coordinates);
        
        // Make the polygon editable only if polygon editing is enabled
        googlePolygon.setEditable(!isPolygonEditingDisabled);
        googlePolygon.setDraggable(false);
        googlePolygon.setOptions({ clickable: false });
        
        // Add event listeners for editing only if polygon editing is enabled
        if (!isPolygonEditingDisabled) {
          const path = googlePolygon.getPath();
          const finishEdit = () => {
            const newCoords: number[][] = [];
            googlePolygon.getPaths().forEach((paths: google.maps.MVCArray) => {
              paths.forEach((path: google.maps.LatLng) => {
                newCoords.push([path.lng(), path.lat()]);
              });
            });

            if (newCoords.length > 0) {
              const updatedClosedPath = [...newCoords, newCoords[0]];
              if (selectedTab === TabsType.market) {
                setProject((prevState) => ({
                  ...prevState,
                  marketPolygons: updatedClosedPath,
                }));
              } else {
                setProject((prevState) => ({
                  ...prevState,
                  soldPolygons: updatedClosedPath,
                  adsPolygons: updatedClosedPath,
                }));
              }
            }
            googlePolygon.setEditable(false);
            setTimeout(() => googlePolygon.setEditable(!isPolygonEditingDisabled), 100);
          };

          path.addListener('set_at', finishEdit);
          path.addListener('insert_at', finishEdit);
        }

        // Add click listener for setting coordinates
        const handlePolygonClick = (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            event.stop();
            setCoordinates({
              lat: event.latLng.lat(),
              lng: event.latLng.lng()
            });
          }
        };
        
        googlePolygon.addListener('click', handlePolygonClick);

        // Update project state and display polygon
        if (selectedTab === TabsType.market) {
          setMarketShapes(googlePolygon);
          googlePolygon.setMap(map);
          setProject((prevState) => ({
            ...prevState,
            marketPolygons: closedPath,
            marketShape: googlePolygon
          }));
        } else {
          setShape(googlePolygon);
          googlePolygon.setMap(map);
          setProject((prevState) => ({
            ...prevState,
            soldPolygons: closedPath,
            adsPolygons: closedPath,
            soldAdsShape: googlePolygon
          }));
        }
        
        clearDrawMode();
        currentPolygonRef.current = googlePolygon;
      }
    },
    [map, selectedTab, setProject, setCoordinates, clearDrawMode, convertTerraDrawToGooglePolygon, isPolygonEditingDisabled]
  );

  const convertPolygonCorsToGoogleMapShape = useCallback((coords: number[][], isMarket: boolean = false) => {
    const polygonCoords = coords.map(coord => ({
      lat: coord[1], // Latitude is second in your array
      lng: coord[0]  // Longitude is first in your array
    }));
    const polygon = new google.maps.Polygon({
      paths: polygonCoords,
      editable: !isPolygonEditingDisabled,
      draggable: false,
      clickable: false,
      fillColor: '#10172a',
      strokeColor: '#10172a',
      strokeOpacity: 1.0,
      strokeWeight: 2,
      fillOpacity: 0.5,
      zIndex: 1,
    });

    // Add listeners for path changes only if polygon editing is enabled
    if (!isPolygonEditingDisabled) {
      const path = polygon.getPath();
      const finishEdit = () => {
        const newCoords: number[][] = [];
        polygon.getPaths().forEach((paths: google.maps.MVCArray) => {
          paths.forEach((path: google.maps.LatLng) => {
            newCoords.push([path.lng(), path.lat()]);
          });
        });

        if (newCoords.length > 0) {
          const closedPath = [...newCoords, newCoords[0]];
          if (isMarket) {
            setProject((prevState) => ({
              ...prevState,
              marketPolygons: closedPath,
            }));
          } else {
            setProject((prevState) => ({
              ...prevState,
              soldPolygons: closedPath,
              adsPolygons: closedPath,
            }));
          }
        }
        polygon.setEditable(false);
        setTimeout(() => polygon.setEditable(!isPolygonEditingDisabled), 100);
      };

      path.addListener('set_at', finishEdit);
      path.addListener('insert_at', finishEdit);
    }
    
    return polygon;
  }, [setProject, isPolygonEditingDisabled]);

  // Initialize TerraDraw when entering draw mode
  useEffect(() => {
    if (map && isDrawModeOn && !isDrawingComplete && !terraDrawRef.current) {
      try {
        const adapter = new TerraDrawGoogleMapsAdapter({
          lib: google.maps,
          map,
          coordinatePrecision: 6
        });

        const draw = new TerraDraw({
          adapter,
          modes: [
            new TerraDrawPolygonMode({
              styles: {
                fillColor: polygonStyle.fillColor,
                fillOpacity: polygonStyle.fillOpacity,
                strokeColor: polygonStyle.strokeColor,
                strokeWidth: polygonStyle.strokeWidth,
                strokeOpacity: polygonStyle.strokeOpacity,
                closingPointColor: '#10172a',
                closingPointWidth: 3,
                closingPointOutlineColor: '#ffffff',
                closingPointOutlineWidth: 2
              }
            }),
            new TerraDrawSelectMode()
          ]
        });

        draw.start();
        draw.setMode('polygon');

        // Listen for polygon creation
        draw.on('finish', (id: string) => {
          // Block center changes immediately
          setIsBlockingCenterChange(true);
          setIsDrawingComplete(true);

          const feature = draw.getSnapshot().find(f => f.id === id);
          if (feature && feature.geometry && feature.geometry.type === 'Polygon' && feature.geometry.coordinates) {
            const coordinates = feature.geometry.coordinates[0];
            // Remove the last coordinate if it's the same as the first (closing coordinate)
            const cleanCoordinates = coordinates.slice(0, -1);
            handlePolygonComplete(cleanCoordinates);
          }
          
          // Unblock center changes after a brief delay
          setTimeout(() => {
            setIsBlockingCenterChange(false);
          }, 100);
        });

        terraDrawRef.current = draw;

      } catch (error) {
        console.error('Error initializing TerraDraw:', error);
      }
    } else if (!isDrawModeOn || isDrawingComplete) {
      if (terraDrawRef.current) {
        try {
          terraDrawRef.current.stop();
          terraDrawRef.current = null;
        } catch (error) {
          console.warn('Error stopping TerraDraw:', error);
        }
      }
    }
  }, [map, isDrawModeOn, isDrawingComplete, handlePolygonComplete]);

  // Handle tab switching
  useEffect(() => {
    // Don't clear draw mode when switching to Price Development tab
    // as it uses a different polygon system (map.data)
    if (selectedTab !== TabsType.priceDevelopment) {
      // Only clear draw mode if we're not actively drawing
      if (!isDrawModeOn || isDrawingComplete) {
        clearDrawMode();
      }
    }
    
    if (selectedTab === TabsType.sold || selectedTab === TabsType.ads) {
      if (shapes) {
        shapes.setMap(map);
      }
      setIsDrawModeOn(!!shapes);
    }
    if (selectedTab === TabsType.market) {
      if (marketShapes) {
        marketShapes.setMap(map);
        setIsDrawingComplete(true);
      }
      setIsDrawModeOn(!!marketShapes);
    }
    
    // Hide regular polygons when on Price Development tab
    if (selectedTab === TabsType.priceDevelopment) {
      if (shapes) {
        shapes.setMap(null);
      }
      if (marketShapes) {
        marketShapes.setMap(null);
      }
    }
  }, [selectedTab, shapes, marketShapes, map]);

  // Handle project changes and polygon loading
  useEffect(() => {
    if (id && map) {
      clearDrawMode();
      setShape(null);
      setMarketShapes(null);
      setIsDrawingComplete(false);
      setIsDrawModeOn(false);
      
      if (selectedTab === TabsType.sold || selectedTab === TabsType.ads) {
        if (soldPolygons) {
          const soldAdsShape = convertPolygonCorsToGoogleMapShape(soldPolygons, false);
          setShape(soldAdsShape);
          soldAdsShape.setMap(map);
          setIsDrawModeOn(true);
          setIsDrawingComplete(true);
        }
        if (marketPolygons) {
          setMarketShapes(convertPolygonCorsToGoogleMapShape(marketPolygons, true));
        }
      } else if (selectedTab === TabsType.market) {
        if (marketPolygons) {
          const marketShape = convertPolygonCorsToGoogleMapShape(marketPolygons, true);
          setMarketShapes(marketShape);
          marketShape.setMap(map);
          setIsDrawModeOn(true);
          setIsDrawingComplete(true);
        }
        if (soldPolygons) {
          setShape(convertPolygonCorsToGoogleMapShape(soldPolygons, false));
        }
      }
    }
  }, [marketPolygons, soldPolygons, map, id, selectedTab, convertPolygonCorsToGoogleMapShape]);

  const startDrawMode = () => {
    if (selectedTab === TabsType.market) {
      setProject((prevState) => ({
        ...prevState,
        marketPolygons: null,
      }));
      setMarketShapes(null);
    } else {
      setProject((prevState) => ({
        ...prevState,
        soldPolygons: null,
        adsPolygons: null,
      }));
      setShape(null);
    }

    // Clear any existing TerraDraw instance
    clearDrawMode();
    setIsDrawingComplete(false);
    setIsDrawModeOn((prevState) => !prevState);
  };

  return {
    isDrawingComplete,
    isDrawModeOn,
    isBlockingCenterChange,
    startDrawMode,
    clearDrawMode,
  };
};

export default useTerraDrawMarkers;